import React, { useState } from 'react';
import clsx from 'clsx';
import Icon from '@components/atoms/icon/icon';
import useUser from '@hooks/user/use-user';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import ToastMessage from '@components/atoms/toast-message/toast-message';
import MyAccountService from '@services/my-account-service';
import RadioButton from '@components/atoms/radio-button/radio-button';
import OptionItem from '@components/atoms/option-item/option-item';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import { useCopyToClipboard } from 'usehooks-ts';
import styles from '../index.module.scss';
import UserSegmentContent from './user-segment-content';
import DeleteAccountButton from './delete-cta';

const BACK_LINK = '/mon-compte.shtml';
const VIP_CONTACT_EMAIL = 'consignment@vestiairecollective.com';
const B2C_CONTACT_EMAIL = 'contactpro@vestiairecollective.com';

const DeleteAccountConfirmation = ({ showBackButton = true }: { showBackButton?: boolean }) => {
  const { t } = useTranslation();
  const { sendAnalyticEvent } = useAnalyticEvents('delete_account');

  const { user, logout } = useUser();
  const { push } = useRouter();
  const [, copy] = useCopyToClipboard();

  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isDeleteConfirm, setIsDeleteConfirm] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showToastMsg, setShowToastMsg] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [confirmation, setConfirmation] = useState<'yes' | 'no' | 'pending'>('pending');
  const [checkboxConfirmed, setCheckboxConfirmed] = useState<boolean>(false);

  const handleCopyEmail = (e: React.MouseEvent, email: string) => {
    e.preventDefault();
    e.stopPropagation();

    sendAnalyticEvent('account_deletion_contact_us');

    copy(email);
    setErrorMessage(null);
    setToastMessage(t('ACCOUNT_DELETION_EMAIL_COPIED'));
    setShowToastMsg(true);
  };

  const confirmCheckboxOptions = [
    {
      value: 'delete-confirm',
      text: t('ACCOUNT_DELETION_DEFAULT_CHECKBOX'),
    },
  ];

  const confirmRadioOptions = [
    {
      value: 'yes',
      text: t('ACCOUNT_DELETION_VIP_CONTENT_YES'),
    },
    {
      value: 'no',
      text: (
        <Trans
          i18nKey="ACCOUNT_DELETION_VIP_CONTENT_NO"
          components={{
            emailLink: (
              <span
                className={styles.deleteAccount__content__body__emailLink}
                onClick={(e: React.MouseEvent) => {
                  handleCopyEmail(e, VIP_CONTACT_EMAIL);
                }}
              >
                {VIP_CONTACT_EMAIL}
              </span>
            ),
          }}
        />
      ),
    },
  ];

  const handleAccountDeletion = async () => {
    sendAnalyticEvent('account_deletion_confirm');

    if (!isDeleteConfirm) {
      return;
    }

    setIsDeleting(true);
    setErrorMessage('');
    setToastMessage('');

    try {
      const success = await MyAccountService.accountClose();
      if (success) {
        sendAnalyticEvent('account_deletion_success');
        logout();
        push('/');
      }
    } catch (error) {
      const errorDetail =
        error.response?.data?.errors?.detail || error.response?.data?.code || t('ACCOUNT_DELETION_GENERIC_ERROR');
      setErrorMessage(errorDetail);
      setToastMessage(errorDetail);
      setShowToastMsg(true);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCheckboxChange = (values) => {
    const isConfirmed = values.length > 0;
    setCheckboxConfirmed(isConfirmed);
    setIsDeleteConfirm(isConfirmed);
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmation(event.target.value as 'yes' | 'no');
    setIsDeleteConfirm(event.target.value === 'yes');
  };

  return (
    <div className={clsx(styles.deleteAccountContainer)}>
      <div className={styles.deleteAccount}>
        {showBackButton && (
          <div
            className={styles.deleteAccount__backCTA}
            onClick={() => {
              window.location.assign(BACK_LINK);
            }}
          >
            <Icon
              className={styles.deleteAccount__backCTA__icon}
              name="ios-arrow-left-16"
              ariaHidden
            />
            <span className={styles.deleteAccount__backCTA__text}>{t('ACCOUNT_DELETION_CTA_BACK')}</span>
          </div>
        )}

        <div className={styles.deleteAccount__content}>
          <h2 className={styles.deleteAccount__content__title}>{t('ACCOUNT_DELETION_TITLE')}</h2>
          <div className={styles.deleteAccount__content__body}>
            <div className={styles.deleteAccount__content__body}>
              {user?.segment === 'B2C' && (
                <UserSegmentContent
                  description={
                    <Trans
                      i18nKey="ACCOUNT_DELETION_B2C_CONTENT"
                      components={{
                        emailLink: (
                          <span
                            className={styles.deleteAccount__content__body__emailLink}
                            onClick={(e: React.MouseEvent) => {
                              handleCopyEmail(e, B2C_CONTACT_EMAIL);
                            }}
                          >
                            {B2C_CONTACT_EMAIL}
                          </span>
                        ),
                      }}
                    />
                  }
                />
              )}
              {user?.segment === 'VIP' && (
                <UserSegmentContent
                  description={t('ACCOUNT_DELETION_VIP_CONTENT')}
                  note={
                    <Trans
                      i18nKey="ACCOUNT_DELETION_VIP_CONTENT_NOTE"
                      components={{
                        faqLink: (
                          <a
                            href={t('ACCOUNT_DELETION_FAQ_LINK')}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                              sendAnalyticEvent('account_deletion_faq');
                            }}
                            className={styles.deleteAccount__content__body__text__note__faqLink}
                          >
                            {'.'}
                          </a>
                        ),
                      }}
                    />
                  }
                >
                  {confirmRadioOptions.map(({ value, text }) => (
                    <RadioButton
                      key={value}
                      id={value}
                      name="yes-confirm"
                      value={value}
                      checked={confirmation === value}
                      onChange={handleRadioChange}
                      label={text}
                    />
                  ))}
                </UserSegmentContent>
              )}

              {user?.segment !== 'B2C' && user?.segment !== 'VIP' && (
                <UserSegmentContent
                  description={
                    <Trans
                      i18nKey="ACCOUNT_DELETION_DEFAULT_CONTENT"
                      components={{
                        faqLink: (
                          <a
                            href={t('ACCOUNT_DELETION_FAQ_LINK')}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                              sendAnalyticEvent('account_deletion_faq');
                            }}
                            className={styles.deleteAccount__content__body__text__description__faqLink}
                          >
                            {'.'}
                          </a>
                        ),
                      }}
                    />
                  }
                >
                  {confirmCheckboxOptions.map(({ value, text }) => (
                    <OptionItem
                      key={value}
                      htmlId={value}
                      label={text}
                      checked={checkboxConfirmed}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        handleCheckboxChange(isChecked ? [{ value, text }] : []);
                      }}
                      useFilterRedesignUi={false}
                      useDesignSystem={true}
                    />
                  ))}
                </UserSegmentContent>
              )}
            </div>
          </div>
        </div>

        {user?.segment !== 'B2C' && (
          <DeleteAccountButton
            onClick={handleAccountDeletion}
            isDeleting={isDeleting}
            isDeleteConfirm={isDeleteConfirm}
          />
        )}
      </div>
      <ToastMessage
        id={'deleteAccountToast'}
        title={toastMessage}
        show={showToastMsg}
        duration={2000}
        className={styles.deleteAccount__toastMessage}
        type={errorMessage ? 'error' : ''}
        icon={errorMessage ? 'warning-20' : 'duplicate-default'}
        onClose={() => setShowToastMsg(false)}
      />
    </div>
  );
};

export default DeleteAccountConfirmation;
